import { Breadcrumbs } from 'ui/atoms/Breadcrumbs'
import { ContainerProps } from './types'
import { InfoIcon } from 'ui/icons'
import { Navigation } from '../Navigation'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'
import { useSelector } from 'react-redux'
import {
  accountStatusSelector,
  isOperatorSelector,
} from 'redux/login/selectors'
import { StatusBadge, Typography, Tooltip } from '@frontend/design_system'
import { useAppSelector } from 'redux/hooks'
import { Loader } from 'ui/atoms/Loader'
import { joinClasses } from 'utils/joinClasses'
import { FavoriteButton } from '../FavoriteButton'
import styles from './styles.module.scss'

export const Container = ({
  breadcrumbList,
  children,
  title,
  currentPageLabel,
  tooltip,
  rightBlock,
  withNavigation,
  companyName,
  additionalComponent,
  withCompanyName = true,
  loading,
  actionButtons,
  favoriteSettings,
}: ContainerProps) => {
  const isOperator = useSelector(isOperatorSelector)
  const { companyId, companyName: supplierCompanyName } = useCompanyInfo(true)

  const accountStatus = useAppSelector(accountStatusSelector)
  const companyIdStr = String(companyId)

  const displayedCompanyName = companyName || supplierCompanyName

  const isShouldDisplayCompanyName =
    withCompanyName && isOperator && displayedCompanyName

  const renderBlock = (
    block: React.ReactNode | ((companyId: string) => React.ReactNode) | null
  ) => {
    if (!block) {
      return null
    }

    if (typeof block === 'function') {
      return <>{block(companyIdStr)}</>
    }

    return <>{block}</>
  }

  return (
    <div className={styles.container} data-testid="container">
      <div className={joinClasses(styles.header, [styles.fixed, !isOperator])}>
        {!!breadcrumbList?.length && currentPageLabel && (
          <div className={styles.breadcrumbs}>
            <Breadcrumbs
              breadcrumbs={breadcrumbList}
              currentPageLabel={currentPageLabel}
            />
            {renderBlock(actionButtons)}
          </div>
        )}
        <div className={styles['title-wrapper']}>
          <div className={styles.title}>
            {typeof title === 'string' ? (
              <Typography Tag="h5" name="H5">
                {title}
              </Typography>
            ) : (
              title
            )}
            {tooltip && (
              <div className={styles['tooltip-wrapper']}>
                <Tooltip
                  size="primary"
                  dataTestId="container-tooltip"
                  popupContent={tooltip}
                  placement="bottom"
                  alignSelf="center"
                >
                  <InfoIcon color="grey400" size="small" />
                </Tooltip>
              </div>
            )}
            {isShouldDisplayCompanyName && (
              <>
                <Typography
                  className={styles['company-badge']}
                  name="Subtitle1"
                  color="blue700"
                  dataTestId="container-company-name"
                >
                  {displayedCompanyName}
                </Typography>
                {accountStatus === 'Approved' && (
                  <div className={styles['status-badge']}>
                    <StatusBadge
                      label={accountStatus}
                      height={30}
                      lineHeight="30px"
                      type="ok"
                      typographyName="Subtitle1"
                      dataTestId="status-badge"
                    />
                  </div>
                )}
                {favoriteSettings && (
                  <div className={styles['favorite-button']}>
                    <FavoriteButton
                      isFavorite={favoriteSettings.isFavorite}
                      onFavoriteChange={favoriteSettings.onFavoriteChange}
                      companyId={companyIdStr}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {renderBlock(rightBlock)}
        </div>
        {withNavigation && <Navigation id={companyIdStr} />}
        {additionalComponent && additionalComponent}
      </div>
      <div className={styles.content}>
        {!loading ? (
          children
        ) : (
          <div className={styles['loader-wrapper']} data-testid="loader">
            <Loader />
          </div>
        )}
      </div>
    </div>
  )
}
